import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  QrCode, Check, Loader2, History, RefreshCw, Gift,
  CheckCircle2, XCircle, AlertCircle, User
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import PartnerLayout from '../../components/partner/PartnerLayout';

const PartnerVerifyPage = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [loadingRedemptions, setLoadingRedemptions] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('partner_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchRedemptions();
  }, []);

  const fetchRedemptions = async () => {
    setLoadingRedemptions(true);
    try {
      const token = localStorage.getItem('partner_token');
      const response = await api.get('/partners/redemptions', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRedemptions(response.data.redemptions || []);
    } catch (error) {
      console.error('Error fetching redemptions:', error);
    } finally {
      setLoadingRedemptions(false);
    }
  };

  const handleValidateCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;

    setValidating(true);
    setValidationResult(null);

    try {
      const token = localStorage.getItem('partner_token');
      const response = await api.post('/partners/validate-code', 
        { code: code.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setValidationResult(response.data);
      if (response.data.success) {
        toast.success('¡Código canjeado exitosamente!');
        setCode('');
        fetchRedemptions();
      }
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.already_redeemed) {
        setValidationResult({
          success: false,
          already_redeemed: true,
          redeemed_at: errorData.redeemed_at,
          redeemed_by_partner: errorData.redeemed_by_partner,
          message: 'Este código ya fue canjeado'
        });
      } else {
        setValidationResult({
          success: false,
          message: errorData?.detail || 'Código no válido'
        });
      }
    } finally {
      setValidating(false);
    }
  };

  return (
    <PartnerLayout currentPage="verificar">
      {/* Header */}
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-3xl font-black text-white">Verificar Código</h1>
        <p className="text-zinc-400">Valida los códigos BRAVX de tus clientes</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Code Validation Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden"
        >
          <div className="p-6 lg:p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#D4AF37] to-[#F59E0B] rounded-2xl flex items-center justify-center mb-6 shadow-[0_0_40px_rgba(212,175,55,0.3)]">
                <QrCode size={40} className="text-black" />
              </div>
              <h2 className="text-xl lg:text-2xl font-extrabold uppercase tracking-tight text-white mb-2">
                Verificar Código
              </h2>
              <p className="text-zinc-400 text-sm">
                Ingresá el código BRAVX del cliente para validar su beneficio
              </p>
            </div>

            {/* Code Input */}
            <form onSubmit={handleValidateCode}>
              <div className="mb-4">
                <Input
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.toUpperCase());
                    setValidationResult(null);
                  }}
                  placeholder="BR-XXXXXX"
                  className="w-full h-16 lg:h-20 bg-zinc-800 border-2 border-zinc-700 rounded-xl text-center text-2xl lg:text-3xl font-mono font-bold tracking-[0.3em] text-white placeholder:text-zinc-600 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30 transition-all uppercase"
                  maxLength={12}
                  autoComplete="off"
                  data-testid="code-input"
                />
              </div>
              
              <Button
                type="submit"
                disabled={validating || !code.trim()}
                className="w-full h-14 lg:h-16 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] text-black text-lg font-bold uppercase tracking-wider rounded-xl transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="validate-btn"
              >
                {validating ? (
                  <Loader2 className="animate-spin" size={24} />
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Check size={24} />
                    Validar Código
                  </span>
                )}
              </Button>
            </form>

            {/* Validation Result */}
            <AnimatePresence mode="wait">
              {validationResult && (
                <motion.div
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.95 }}
                  className={`mt-6 p-5 rounded-xl border-2 ${
                    validationResult.success
                      ? 'bg-green-500/10 border-green-500/30'
                      : 'bg-red-500/10 border-red-500/30'
                  }`}
                  data-testid="validation-result"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      validationResult.success ? 'bg-green-500' : 'bg-red-500'
                    }`}>
                      {validationResult.success ? (
                        <CheckCircle2 size={24} className="text-white" />
                      ) : (
                        <XCircle size={24} className="text-white" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-bold text-lg mb-1 ${
                        validationResult.success ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {validationResult.success ? '¡Código Válido!' : validationResult.message}
                      </h3>

                      {validationResult.success && (
                        <div className="mt-4 space-y-3">
                          {validationResult.benefit && (
                            <div className="bg-black/30 rounded-lg p-3">
                              <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Beneficio</p>
                              <p className="text-white font-bold">{validationResult.benefit.title}</p>
                            </div>
                          )}
                          {validationResult.user && (
                            <div className="bg-black/30 rounded-lg p-3">
                              <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Cliente</p>
                              <div className="flex items-center gap-2">
                                <User size={16} className="text-[#D4AF37]" />
                                <p className="text-white font-semibold">{validationResult.user.name}</p>
                              </div>
                              {validationResult.user.dni && (
                                <p className="text-zinc-400 text-sm font-mono mt-1">DNI: {validationResult.user.dni}</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {validationResult.already_redeemed && (
                        <p className="text-zinc-400 text-sm mt-2">
                          Canjeado: {new Date(validationResult.redeemed_at).toLocaleString('es-AR')}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Recent Redemptions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden"
        >
          <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
            <h3 className="font-bold text-lg text-white flex items-center gap-3">
              <History size={20} className="text-[#D4AF37]" />
              Historial de Canjes
            </h3>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={fetchRedemptions}
              className="text-zinc-400 hover:text-white"
              data-testid="refresh-btn"
            >
              <RefreshCw size={16} />
            </Button>
          </div>

          <div className="p-4 max-h-[500px] overflow-y-auto">
            {loadingRedemptions ? (
              <div className="flex justify-center py-10">
                <Loader2 className="animate-spin text-[#D4AF37]" size={28} />
              </div>
            ) : redemptions.length === 0 ? (
              <div className="text-center py-10">
                <Gift size={40} className="mx-auto text-zinc-700 mb-3" />
                <p className="text-zinc-500">No hay canjes registrados</p>
              </div>
            ) : (
              <div className="space-y-3">
                {redemptions.map((r, index) => (
                  <motion.div 
                    key={r.id} 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-3 bg-zinc-800/50 rounded-lg flex items-center justify-between hover:bg-zinc-800 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-green-500/10 rounded-lg flex items-center justify-center">
                        <Check size={16} className="text-green-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white text-sm">{r.benefit_title}</p>
                        <p className="text-zinc-500 text-xs">{r.user_name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-zinc-600 text-xs font-mono">{r.code}</p>
                      <p className="text-zinc-600 text-xs">
                        {new Date(r.created_at).toLocaleDateString('es-AR')}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </PartnerLayout>
  );
};

export default PartnerVerifyPage;
